import { useDisclosure } from '@heroui/react';
import React, { useCallback } from 'react';
import { FiTrash2 } from 'react-icons/fi';

import { BaseButton } from '../../../../components/BaseButton';
import { BaseModal } from '../../../../components/BaseModal';
import { useNotification } from '../../../../contexts/NotificationContext';
import { useDeleteUser } from '../../hooks';

export interface DeleteUserProps {
  userId: string;
  userName?: string;
  hasRequestedToBeDeleted: boolean;
  onDeleted?: () => void;
  disabled?: boolean;
}

export const DeleteUser: React.FC<DeleteUserProps> = ({
  userId,
  userName,
  hasRequestedToBeDeleted,
  onDeleted,
  disabled = false,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { addNotification } = useNotification();

  const { deleteUser, isLoading } = useDeleteUser({
    captureException: (error) => {
      addNotification({
        title: 'Error Deleting User',
        message: error.message || 'Failed to delete user. Please try again.',
        type: 'error',
      });
    },
  });

  const handleDeleteUser = useCallback(async () => {
    try {
      const success = await deleteUser({ userId });

      if (success) {
        addNotification({
          title: 'User Deleted',
          message: `User ${
            userName ? `"${userName}" ` : ''
          }has been marked for deletion successfully.`,
          type: 'success',
        });
        onDeleted?.();
        onClose();
      } else {
        throw new Error('Delete operation failed');
      }
    } catch (error) {
      // Error handling is done in the hook's captureException
    }
  }, [deleteUser, userId, userName, onDeleted, onClose, addNotification]);

  const handleOpenModal = useCallback(() => {
    // Track analytics if needed
    // trackSegmentAction({ event: 'user-delete-account-clicked' });
    onOpen();
  }, [onOpen]);

  const handleCancelDelete = useCallback(() => {
    // Track analytics if needed
    // trackSegmentAction({ event: 'user-delete-cancel' });
    onClose();
  }, [onClose]);

  const isDeleteDisabled = disabled || hasRequestedToBeDeleted || isLoading;

  return (
    <>
      <div className="flex justify-end py-4">
        <BaseButton
          isDestructive
          variant="bordered"
          startContent={<FiTrash2 className="h-4 w-4" />}
          onPress={handleOpenModal}
          isDisabled={isDeleteDisabled}
        >
          {hasRequestedToBeDeleted ? 'DELETION REQUESTED' : 'DELETE ACCOUNT'}
        </BaseButton>
      </div>

      <BaseModal
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        title="Delete User Account"
        footerContent={{
          onCancel: handleCancelDelete,
          onConfirm: handleDeleteUser,
          isLoading,
          isDisabled: isLoading,
          isDestructive: true,
          confirmText: isLoading ? 'Deleting...' : 'Delete Account',
        }}
      >
        <div className="text-foreground">
          <p className="mb-4">
            Are you sure you want to delete{' '}
            {userName ? `"${userName}"'s` : 'this'} user account?
          </p>
          <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
            <p className="text-danger-800 text-sm font-medium">
              ⚠️ This action cannot be undone
            </p>
            <p className="text-danger-700 text-sm mt-1">
              All user data will be lost and cannot be recovered.
            </p>
          </div>
        </div>
      </BaseModal>
    </>
  );
};
